import * as React from 'react';
import { Form, Field, FormElement } from '@progress/kendo-react-form';
import { Button } from '@progress/kendo-react-buttons';
import { Input } from '../components/form/Input';
import { CustomDatePicker } from '../components/form/CustomDatePicker';
import { Switch, RadioGroup, TextArea } from '@progress/kendo-react-inputs';
import styled, { createGlobalStyle } from 'styled-components';
import { Card, CardBody, TabStrip, TabStripTab } from '@progress/kendo-react-layout';
import { trashIcon } from '@progress/kendo-svg-icons';
import { Loader } from '@progress/kendo-react-indicators';
import config from '../config/config';

/* ---------- UI-Hilfskomponenten ---------- */
import GridJobs from '../components/Grid-Jobs';
import AnmerkungenTextArea from '../components/AnmerkungenTextArea';
import MapModal from '../components/MapModal';
import ContactPersonsModal from '../components/ContactPersonsModal';
import EmployeeAssignmentModal from '../components/EmployeeAssignmentModal';
import JobDocumentsModal from '../components/JobDocuments';

const GlobalStyle = createGlobalStyle`
  .pac-container { z-index: 10000 !important; }
`;
const commonInputStyle = { width: '300px' };

const SwitchField = ({ value, onChange, label }) => (
  <div style={{ marginTop: 20 }}>
    <label style={{ marginRight: 10 }}>{label}:</label>
    <Switch checked={value} onChange={(e) => onChange({ value: e.value })} />
  </div>
);

const StyledTabStrip = styled(TabStrip)`
  .k-tabstrip-items-wrapper .k-item         { background:#99a4ae!important; }
  .k-tabstrip-items-wrapper .k-link         { color:white!important; }
  .k-tabstrip-items-wrapper .k-item.k-active,
  .k-tabstrip-items-wrapper .k-item.k-state-active,
  .k-tabstrip-items-wrapper .k-item.k-selected { background:#f5811e!important; }
  .k-tabstrip-items-wrapper .k-item:not(.k-active):hover { background:#f2f2f2!important; }
  .k-tabstrip-items-wrapper .k-item:not(.k-active):hover .k-link { color:#f5811e!important; }
`;

/**
 *  Pure Presentational – keinerlei Business-Logik.
 *  Alles Notwendige kommt via Props rein.
 */
const EditJobsView = (props) => {
  const {
    /* Flags & Daten ------------- */
    jobID,
    loading, jobData, users, descriptionOptions, dataState, sort, imagePreviews,
    contactPersons, jobDocuments, selectedTab, selectedValue, summaryText,
    pdfLoading, mapVisible, contactPersonsVisible, employeeAssignmentVisible,
    jobDocumentsVisible, isGoogleMapsLoaded, radioData, noteOptions,
    showNotification,
    /* Setter / Callbacks -------- */
    formRef, setMapVisible, setContactPersonsVisible, setEmployeeAssignmentVisible,
    setJobDocumentsVisible, setDataState, setSort, setImagePreviews, setContactPersons,
    setJobDocuments, setSelectedTab, setSelectedValue, setSummaryText,
    /* Handler ------------------- */
    handleTabSelect, onCancelClick, onSubmit, downloadPdf, removeImage, onRowAction
  } = props;

  /* ---------- Loader ---------- */
  if (loading || !jobData) {
    return (
      <div style={{ display:'flex',flexDirection:'column',alignItems:'center',marginTop:100 }}>
        <Loader size="large" type="infinite-spinner" />
        <p>Job wird geladen…</p>
      </div>
    );
  }

  /* ---------- UI ---------- */
 return (
  <>
    <GlobalStyle />
    <div id="EditJobs" className="profile-page main-content">
      <div className="card-container">

        {/* Titel */}
        <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20 }}>
          <h3 className="card-title">Auftragsmanagement</h3>
        </div>

        <div className="card-component">
          <Form
            ref={formRef}
            initialValues={jobData}
            onSubmit={onSubmit}
            render={(formProps) => {
              /* ------------------------------------------------------ */
              /* Helper: Datumslogik – Enddatum = Start + 8 Stunden     */
              /* ------------------------------------------------------ */
              const setDate = (field) => (e) => {
                let d = e?.value ?? null;
                if (typeof d === 'string' && d.trim()) d = new Date(d);
                if (!(d instanceof Date) || isNaN(d)) d = null;

                formProps.onChange(field, { value: d });

                if (field === 'AusfuehrungszeitStart' && d) {
                  const endDate = new Date(d.getTime() + 8 * 60 * 60 * 1000);
                  formProps.onChange('AusfuehrungszeitEnde', { value: endDate });
                }
              };

              return (
                <FormElement horizontal style={{ maxWidth: 920 }}>
                  {/* --- Kunde & Auftrag --- */}
                  <div style={{ display:'flex',gap:100 }}>
                    <div style={{ flex:1 }}>
                      <h3>Kundendaten</h3>
                      <Field id="firma" name="firma" label="Firma" component={Input}
                             value={jobData.firma} style={commonInputStyle} disabled />
                      <Field id="ansprechpartner" name="ansprechpartner" label="Ansprechpartner"
                             component={Input} value={jobData.ansprechpartner}
                             style={commonInputStyle} disabled />
                      <Field id="adresse" name="adresse" label="Adresse" component={Input}
                             value={jobData.adresse} style={commonInputStyle} disabled />
                    </div>
                    <div style={{ flex:1 }}>
                      <h3 style={{ marginLeft:-20 }}>Auftragsdaten</h3>
                      <Field id="AusfuehrungszeitStart" name="AusfuehrungszeitStart"
                             label="Startdatum und Uhrzeit" component={CustomDatePicker}
                             format="dd.MM.yyyy HH:mm" timeFormat="HH:mm" style={commonInputStyle}
                             onChange={setDate('AusfuehrungszeitStart')} />
                      <Field id="AusfuehrungszeitEnde" name="AusfuehrungszeitEnde"
                             label="Enddatum und Uhrzeit" component={CustomDatePicker}
                             format="dd.MM.yyyy HH:mm" timeFormat="HH:mm" style={commonInputStyle}
                             onChange={setDate('AusfuehrungszeitEnde')} />
                      <Field id="auftragsnummer" name="auftragsnummer" label="Auftragsnummer"
                             component={Input} style={commonInputStyle} />
                      <Field id="kurzbeschreibung" name="kurzbeschreibung" label="Kurzbeschreibung"
                             component={Input} style={commonInputStyle} />
                    </div>
                  </div>

                  <hr />

                  {/* --- Einsatzort & Optionen --- */}
                  <div style={{ display:'flex',gap:100,marginTop:20 }}>
                    <div style={{ flex:1 }}>
                      <h3>Einsatzort</h3>
                      {/* Straße etc. */}
                      {['strasse','hausnummer','plz','ort','latitude','longitude'].map((f,i) => (
                        <Field key={f} id={f} name={f}
                               label={['Straße','Hausnummer','PLZ','Stadt','Breitengrad','Längengrad'][i]}
                               component={Input} style={commonInputStyle} />
                      ))}
                    </div>

                    {/* Map-Modal */}
                    {mapVisible && (
                      <MapModal
                        visible={mapVisible}
                        defaultLat={formProps.valueGetter('latitude')}
                        defaultLng={formProps.valueGetter('longitude')}
                        onClose={() => setMapVisible(false)}
                        onSave={async (lat, lng) => {
                          /* Koordinaten setzen -------------------- */
                          formProps.onChange('latitude',  { value: lat });
                          formProps.onChange('longitude', { value: lng });

                          /* Reverse Geocoding via Google API ------ */
                          try {
                            const res   = await fetch(
                              `https://maps.googleapis.com/maps/api/geocode/json` +
                              `?latlng=${lat},${lng}&key=${config.maps_api_key}`
                            );
                            const data  = await res.json();
                            if (data.status === 'OK' && data.results.length) {
                              const comps = data.results[0].address_components;
                              let street='', house='', postal='', city='';
                              comps.forEach(c => {
                                if (c.types.includes('route'))          street = c.long_name;
                                if (c.types.includes('street_number'))  house  = c.long_name;
                                if (c.types.includes('postal_code'))    postal = c.long_name;
                                if (c.types.includes('locality') ||
                                    c.types.includes('postal_town'))    city   = c.long_name;
                              });
                              formProps.onChange('strasse',    { value: street });
                              formProps.onChange('hausnummer', { value: house });
                              formProps.onChange('plz',        { value: postal });
                              formProps.onChange('ort',        { value: city   });
                            }
                          } catch (err) {
                            console.error('Reverse Geocoding fehlerhaft:', err);
                            /* Absichtlich kein Abbruch – Koordinaten hast du ja schon */
                          }

                          setMapVisible(false);
                        }}
                        isLoaded={isGoogleMapsLoaded}
                      />
                    )}

                    <div style={{ flex:1 }}>
                      <h3>Optionen</h3>
                      <div style={{ display:'flex',flexDirection:'column',gap:10 }}>
                        <Field name="JobActive" label="Job aktiv" component={SwitchField} />
                        <Button onClick={downloadPdf}>PDF Download</Button>
                        {pdfLoading && <Loader type="converging-spinner" style={{ marginTop:10 }} />}
                        <Button type="button" onClick={() => setMapVisible(true)}>GoogleMaps</Button>
                        <Button onClick={() => setContactPersonsVisible(true)}>Ansprechpartner</Button>
                        <Button onClick={() => setEmployeeAssignmentVisible(true)}>Mitarbeiterzuweisung</Button>
                        <Button onClick={() => setJobDocumentsVisible(true)}>Jobdokumente</Button>
                      </div>
                    </div>
                  </div>

                  <hr />

                  {/* --- Notizen --- */}
                  <div style={{ display: 'flex', gap: 20, marginTop: 20 }}>
                    <div style={{ flex: 1 }}>
                      <h3>Notizen</h3>
                      <div style={{ marginLeft: 110, marginBottom: 20 }}>
                        <Field
                          name="InternalNotes"
                          component={TextArea}
                          placeholder="Interne Notizen hier eingeben…"
                          style={{ width: '100%', height: 150, marginTop: 10 }}
                        />
                      </div>
                    </div>
                  </div>


                  <hr />

                  {/* --- Tabstrip --- */}
                  <h3 style={{ marginTop:20 }}>Jobdaten</h3>
                  <StyledTabStrip
                    selected={selectedTab}
                    onSelect={handleTabSelect}
                    keepTabsMounted
                    renderAllContent
                    animation={false}
                    style={{ marginTop:50 }}
                  >
                    {/* Aufgaben */}
                    <TabStripTab title="Aufgaben">
                      <CardBody style={{ marginTop:20 }}>
                        <GridJobs
                          dataState={dataState}
                          sort={sort}
                          setSort={setSort}
                          onRowAction={onRowAction}
                          descriptionOptions={descriptionOptions}
                          setDataState={setDataState}
                        />
                      </CardBody>
                    </TabStripTab>

                    {/* Fotodoku */}
                    <TabStripTab title="Fotodoku">
                      <div style={{ display:'flex',flexWrap:'wrap',gap:15,justifyContent:'center',margin:'20px 0' }}>
                        {imagePreviews.map((src,idx) => (
                          <Card key={idx} style={{ width:180,boxShadow:'0 0 4px rgba(0,0,0,.1)' }}>
                            <img src={src} alt={`Bild ${idx+1}`} style={{ height:185,maxWidth:'100%' }} />
                            <CardBody style={{ display:'flex',justifyContent:'space-between',alignItems:'center' }}>
                              <h5 style={{ margin:0 }}>Bild {idx+1}</h5>
                              <Button fillMode="flat" onClick={() => removeImage(idx)} svgIcon={trashIcon} />
                            </CardBody>
                          </Card>
                        ))}
                      </div>
                    </TabStripTab>

                    {/* Anmerkungen */}
                    <TabStripTab title="Anmerkungen">
                      <div style={{ margin:20 }}>
                        <Field name="Anmerkungen" component={AnmerkungenTextArea} />
                      </div>
                    </TabStripTab>

                    {/* Kampfmittelbefund */}
                    <TabStripTab title="Kampfmittelbefund">
                      <div style={{ margin:20 }}>
                        <h3>Kampfmittelbefund</h3>
                        <RadioGroup
                          data={radioData}
                          value={selectedValue}
                          onChange={(e) => {
                            setSelectedValue(e.value);
                            formProps.onChange('Summary', { value: radioData.find(o => o.value===e.value)?.label });
                          }}
                          style={{ width:'100%' }}
                        />
                        {noteOptions.includes(selectedValue) && (
                          <div style={{ marginTop:20 }}>
                            <label>Anmerkungen</label>
                            <Field
                              name="SummaryNotes"
                              component={TextArea}
                              value={summaryText}
                              onChange={(e) => {
                                setSummaryText(e.value);
                                formProps.onChange('SummaryNotes',{ value:e.value });
                              }}
                              placeholder="Bitte geben Sie den Grund an…"
                              style={{ width:'100%',height:150 }}
                            />
                          </div>
                        )}
                      </div>
                    </TabStripTab>
                  </StyledTabStrip>

                  <hr />

                  {/* --- Buttons --- */}
                  <div className="k-form-buttons">
                    <Button onClick={onCancelClick}>Abbrechen</Button>
                    <Button themeColor="primary" type="submit" disabled={!formProps.allowSubmit}>
                      Speichern
                    </Button>
                  </div>
                </FormElement>
              );
            }}
          />

          {/* ---------- Modals ---------- */}
          <ContactPersonsModal
            visible={contactPersonsVisible}
            onClose={(list) => { if (list) setContactPersons(list); setContactPersonsVisible(false);} }
            jobID={jobID}
            initialContactPersons={contactPersons}
            showNotification={showNotification}
          />

          <EmployeeAssignmentModal
            visible={employeeAssignmentVisible}
            onClose={() => setEmployeeAssignmentVisible(false)}
            jobID={jobID}
            initialAssignedEmployees={jobData?.AssignedEmployee}
            showNotification={showNotification}
            users={users}
          />

          <JobDocumentsModal
            visible={jobDocumentsVisible}
            onClose={(docs) => { if (docs) setJobDocuments(docs); setJobDocumentsVisible(false);} }
            jobID={jobID}
            initialDocs={jobDocuments}
          />
        </div>
      </div>
    </div>
  </>
);

};

export default EditJobsView;

